// texe-web/src/components/CommentsPanel.tsx
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { API_BASE_URL, apiDelete, apiGet, apiPost, apiPatch, Admin } from "../lib/api";
import { searchUsers, type UserSearchItem } from "../lib/userSearchApi";
import { useAuth } from "../context/AuthProvider";
import CommentLikeButton from "./CommentLikeButton";
import Avatar from "./Avatar";
import ImageModal from "./ImageModal";
import { realtimeBus } from "../realtime/RealtimeBus";
import TranslateButton from "./TranslateButton";
import RichText from "./RichText";

type CommentItem = {
  id: string;
  text: string;
  createdAt: string;
  author?: {
    id?: string;
    username?: string;
    avatarUrl?: string | null;
    avatarUrlSmall?: string | null;
    roles?: string[];
    isAdmin?: boolean;
  } | null;
  highlight?: "gold" | null;
};

function abs(url?: string | null): string | undefined {
  if (!url) return undefined;
  return url.startsWith("/") ? `${API_BASE_URL}${url}` : url;
}

export default function CommentsPanel({ postId }: { postId: string }) {
  const { user } = useAuth();

  // 👉 Me/Yetki bilgileri
  const myId = (user as any)?.id ?? (user as any)?._id ?? null;
  const myRoles: string[] = Array.isArray((user as any)?.roles) ? (user as any).roles : [];
  const myIsAdmin: boolean = Boolean((user as any)?.isAdmin) || myRoles.includes("admin");
  const myIsModerator: boolean = myRoles.includes("moderator");

  const [items, setItems] = useState<CommentItem[]>([]);
  const [nextAfter, setNextAfter] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [moreLoading, setMoreLoading] = useState(false);

  const [text, setText] = useState("");
  const [sendBusy, setSendBusy] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const [busy, setBusy] = useState(false);

  const [err, setErr] = useState<string | null>(null);
  const [sendErr, setSendErr] = useState<string | null>(null);
  const [listError, setListError] = useState<string | null>(null);

  const [modal, setModal] = useState<{ open: boolean; src?: string; username?: string }>({
    open: false,
  });

  // mention (yeni yorum inputu)
  const inRef = useRef<HTMLInputElement | null>(null);
  const [mOpen, setMOpen] = useState(false);
  const [_mQ, setMQ] = useState("");
  const [mItems, setMItems] = useState<UserSearchItem[]>([]);
  const [mIdx, setMIdx] = useState(-1);

  if (!postId) return null;

  async function load(initial = false) {
    const qs =
      initial || !nextAfter ? `?postId=${postId}` : `?postId=${postId}&after=${encodeURIComponent(nextAfter)}`;

    const res = await apiGet<{ ok: true; items: CommentItem[]; nextAfter: string | null }>(
      `/api/comments${qs}`
    );
    setItems((curr) => (initial ? res.items : [...curr, ...res.items]));
    setNextAfter(res.nextAfter);
  }

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setListError(null);
        await load(true);
      } catch (e: any) {
        console.error("Comments load failed:", e);
        setListError(e?.message || "Yorumlar yüklenemedi.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    // 🔔 Tek SSE kanalı üzerinden postId bazlı yorum event’leri
    const offNew = realtimeBus.onCommentNew(postId, ({ comment }) => {
      if (!comment?.id) return;
      setItems((curr) => (curr.some((x) => x.id === comment.id) ? curr : [comment, ...curr]));
    });
    const offEdit = realtimeBus.onCommentEdit(postId, ({ comment }) => {
      if (!comment?.id) return;
      setItems((curr) => curr.map((x) => (x.id === comment.id ? { ...x, ...comment } : x)));
    });
    const offDel = realtimeBus.onCommentDelete(postId, ({ commentId }) => {
      if (!commentId) return;
      setItems((curr) => curr.filter((x) => x.id !== commentId));
    });

    return () => {
      cancelled = true;
      offNew();
      offEdit();
      offDel();
      setEditingId(null);
      setEditText("");
      setErr(null);
      setBusy(false);
      setSendBusy(false);
      setSendErr(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postId]);

  async function loadMore() {
    if (!nextAfter || moreLoading) return;
    setMoreLoading(true);
    try {
      await load(false);
    } finally {
      setMoreLoading(false);
    }
  }

  function isMine(c: CommentItem) {
    if (!user || !c.author) return false;
    const byId = c.author.id && myId && String(c.author.id) === String(myId);
    const byUsername =
      c.author.username && (user as any)?.username && String(c.author.username) === String((user as any).username);
    return Boolean(byId || byUsername);
  }

  // ✅ Yetki matrisi (frontend)
  // - Düzenle: sahibi veya admin
  // - Sil: sahibi veya admin veya moderatör
  function canEdit(c: CommentItem) {
    return isMine(c) || myIsAdmin;
  }
  function canDelete(c: CommentItem) {
    return isMine(c) || myIsAdmin || myIsModerator;
  }

  async function submit() {
    const trimmed = text.trim();
    if (!trimmed || sendBusy) return;

    setSendBusy(true);
    setSendErr(null);
    try {
      const res = await apiPost<{ ok: true; item: CommentItem }>("/api/comments", {
        postId,
        text: trimmed,
      });
      setItems((curr) => (curr.some((x) => x.id === res.item.id) ? curr : [res.item, ...curr]));
      setText("");
    } catch (e: any) {
      console.error(e);
      setSendErr(e?.message || "Yorum gönderilemedi.");
    } finally {
      setSendBusy(false);
    }
  }

  async function remove(id: string) {
    const c = items.find((x) => x.id === id);
    if (!c || !canDelete(c)) return;
    if (!confirm("Yorumu silmek istiyor musun?")) return;
    await apiDelete(`/api/comments/${id}`);
    setItems((curr) => curr.filter((c) => c.id !== id));
    if (editingId === id) {
      setEditingId(null);
      setEditText("");
      setErr(null);
      setBusy(false);
    }
  }

  function startEdit(c: CommentItem) {
    if (!canEdit(c) || busy) return;
    setErr(null);
    setEditingId(c.id);
    setEditText(c.text);
  }

  function cancelEdit() {
    setEditingId(null);
    setEditText("");
    setErr(null);
    setBusy(false);
  }

  async function saveEdit() {
    if (!editingId || busy) return;
    const c = items.find((x) => x.id === editingId);
    if (!c || !canEdit(c)) return;

    const trimmed = editText.trim();
    if (!trimmed) {
      setErr("Yorum boş olamaz.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      // 🚀 FAZ 8: Admin ise admin endpoint kullan, değilse normal kullanıcı endpoint'i
      let res: { ok: true; item?: CommentItem; comment?: CommentItem };
      if (myIsAdmin) {
        // Admin endpoint: PUT /api/admin/comments/:id
        const adminRes = await Admin.editCommentDirect(editingId, trimmed.slice(0, 500));
        res = { ok: true, item: adminRes.comment };
      } else {
        // Normal user endpoint: PATCH /api/comments/:id
        res = await apiPatch<{ ok: true; item: CommentItem }>(`/api/comments/${editingId}`, {
          text: trimmed.slice(0, 500),
        });
      }
      
      const updatedComment = res.item || res.comment;
      if (updatedComment) {
        setItems((curr) => curr.map((c) => (c.id === editingId ? updatedComment : c)));
      }
      cancelEdit();
    } catch (e: any) {
      const msg = e?.message && e.message.length < 200 ? e.message : "Kaydetme başarısız.";
      setErr(msg);
    } finally {
      setBusy(false);
    }
  }

  // ───── Admin: yoruma 1000 like bas ─────
  const [boostingId, setBoostingId] = useState<string | null>(null);
  async function boostComment(id: string) {
    if (!myIsAdmin || boostingId) return;
    try {
      setBoostingId(id);
      await apiPost<{ ok: true; boosted: true; count: number }>(`/api/likes/boost/comment/${id}`, {});
      // Like sayacı CommentLikeButton içinde; zorla refresh için küçük bir hile:
      // ilgili CommentLikeButton yeniden mount olsun diye state'i "dokunduralım"
      setItems((curr) => curr.map((c) => (c.id === id ? { ...c } : c)));
    } catch (e) {
      console.error("boost failed", e);
      alert("Boost işlemi başarısız oldu.");
    } finally {
      setBoostingId(null);
    }
  }

  // ───── Mention input ─────
  function _caretPos() {
    const el = inRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    return { top: rect.top + window.scrollY + 32, left: rect.left + window.scrollX + 12 };
  }
  function maybeOpenMentionInput() {
    const el = inRef.current;
    if (!el) return;
    const pos = el.selectionStart ?? el.value.length;
    const before = el.value.slice(0, pos);
    const match = /(^|[\s.,;:!?()[\]{}"'])@([A-Za-z0-9_.]{0,20})$/.exec(before);
    if (match) {
      const q = match[2] || "";
      setMQ(q);
      setMOpen(true);
      debouncedFetch(q);
    } else {
      setMOpen(false);
      setMQ("");
      setMItems([]);
      setMIdx(-1);
    }
  }
  const fetchTimer = useRef<number | null>(null);
  function debouncedFetch(q: string) {
    if (fetchTimer.current) window.clearTimeout(fetchTimer.current);
    fetchTimer.current = window.setTimeout(async () => {
      const items = q ? await searchUsers(q, 8) : [];
      setMItems(items);
    }, 200);
  }
  function insertUsernameInput(username: string) {
    const el = inRef.current;
    if (!el) return;
    const pos = el.selectionStart ?? el.value.length;
    const before = el.value.slice(0, pos);
    const after = el.value.slice(pos);
    const startMatch = /(^|[\s.,;:!?()[\]{}"'])(@([A-Za-z0-9_.]{0,20}))$/.exec(before);
    const startIdx = startMatch ? pos - (startMatch[2]?.length ?? 0) : pos;
    const prefix = el.value.slice(0, startIdx);
    const newVal = `${prefix}@${username} ${after}`;
    const newCaret = (prefix + "@" + username + " ").length;

    setText(newVal);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(newCaret, newCaret);
    });

    setMOpen(false);
    setMQ("");
    setMItems([]);
    setMIdx(-1);
  }
  function onKeyInput(e: React.KeyboardEvent<HTMLInputElement>) {
    if (mOpen) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setMIdx((v) => Math.min(mItems.length - 1, v + 1));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setMIdx((v) => Math.max(-1, v - 1));
        return;
      }
      if (e.key === "Enter") {
        if (mIdx >= 0 && mItems[mIdx]) {
          e.preventDefault();
          insertUsernameInput(mItems[mIdx].username);
          return;
        }
      }
      if (e.key === "Escape") {
        setMOpen(false);
        return;
      }
    }
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "enter") {
      e.preventDefault();
      void submit();
    }
  }

  if (loading) return <div style={{ padding: 8, opacity: 0.7 }}>Yorumlar yükleniyor...</div>;
  if (listError) return <div style={{ padding: 8, color: "crimson" }}>{listError}</div>;

  const canSend = text.trim().length > 0 && !sendBusy;

  return (
    <div style={{ marginTop: 10, borderTop: "1px solid #333", paddingTop: 8 }}>
      {user && (
        <div style={{ position: "relative", display: "flex", gap: 8, marginBottom: 8 }}>
          <input
            ref={inRef}
            value={text}
            onChange={(e) => {
              setText(e.target.value.slice(0, 500));
              setTimeout(() => maybeOpenMentionInput(), 0);
            }}
            onKeyDown={onKeyInput}
            onKeyUp={() => maybeOpenMentionInput()}
            placeholder="Yorum yaz... (@kullanici ile bahset)"
            style={{ flex: 1, padding: 8, borderRadius: 6 }}
            maxLength={500}
          />
          <button onClick={submit} disabled={!canSend} aria-disabled={!canSend} title="Gönder (Ctrl/Cmd + Enter)">
            {sendBusy ? "Gönderiliyor…" : "Gönder"}
          </button>

          {/* mention dropdown */}
          {mOpen && (
            <div
              role="listbox"
              style={{
                position: "absolute",
                top: 38,
                left: 0,
                background: "#0b0b0b",
                border: "1px solid #222",
                borderRadius: 10,
                padding: 6,
                boxShadow: "0 10px 30px rgba(0,0,0,0.35)",
                maxHeight: 280,
                overflow: "auto",
                zIndex: 25,
                width: "min(360px, 100%)",
              }}
            >
              {mItems.length === 0 && <div style={{ padding: 8, opacity: 0.7 }}>Kullanıcı bulunamadı</div>}
              {mItems.map((u, i) => (
                <div
                  key={u.id}
                  role="option"
                  aria-selected={i === mIdx}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    insertUsernameInput(u.username);
                  }}
                  onMouseEnter={() => setMIdx(i)}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "28px 1fr",
                    gap: 8,
                    alignItems: "center",
                    padding: 6,
                    borderRadius: 8,
                    background: i === mIdx ? "#121212" : "transparent",
                    cursor: "pointer",
                  }}
                >
                  <Avatar src={abs(u.avatarUrlSmall || u.avatarUrl)} alt={u.username} size={28} />
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontWeight: 600,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      @{u.username}
                    </div>
                    {u.bio && (
                      <div
                        style={{
                          opacity: 0.75,
                          fontSize: 12,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {u.bio}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {sendErr && <div style={{ color: "crimson", marginBottom: 8 }}>{sendErr}</div>}

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {items.length === 0 && <div style={{ opacity: 0.7 }}>Henüz yorum yok.</div>}
        {items.map((c) => {
          const editing = editingId === c.id;

          const small = abs(c.author?.avatarUrlSmall || c.author?.avatarUrl);
          const big = abs(c.author?.avatarUrl || c.author?.avatarUrlSmall);

          // Gold tespiti + Yetkiler
          const isGold =
            c.highlight === "gold" ||
            !!c.author?.isAdmin ||
            (Array.isArray(c.author?.roles) &&
              (c.author.roles.includes("admin") || c.author.roles.includes("moderator")));

          const allowEdit = canEdit(c);
          const allowDelete = canDelete(c);

          const isBoosting = boostingId === c.id;

          return (
            <div
              id={`comment-${c.id}`}
              key={c.id}
              style={{
                fontSize: 14,
                background: isGold
                  ? "linear-gradient(180deg, rgba(34,27,0,0.35) 0%, rgba(20,20,20,0.6) 100%)"
                  : "#111",
                border: isGold ? "1px solid rgba(255,215,0,0.7)" : "1px solid #222",
                boxShadow: isGold
                  ? "0 0 0 1px rgba(255,215,0,0.15), 0 0 12px rgba(255,215,0,0.08)"
                  : "none",
                borderRadius: 10,
                padding: 8,
                display: "grid",
                gridTemplateColumns: "40px 1fr",
                gap: 10,
                alignItems: "flex-start",
              }}
            >
              <div
                onClick={() =>
                  big && setModal({ open: true, src: big, username: c.author?.username || undefined })
                }
                title={big ? "Büyüt" : undefined}
                style={{
                  display: "flex",
                  justifyContent: "center",
                  paddingTop: 2,
                  cursor: big ? "zoom-in" : "default",
                }}
              >
                <Avatar src={small} alt={c.author?.username || "avatar"} size={36} />
              </div>

              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    opacity: 0.9,
                    fontSize: 12,
                    display: "flex",
                    gap: 8,
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <strong>
                    {c.author?.username ? (
                      <Link
                        to={`/u/${encodeURIComponent(c.author.username)}`}
                        style={{ textDecoration: "none", color: isGold ? "gold" : "inherit" }}
                      >
                        @{c.author.username}
                      </Link>
                    ) : (
                      "Anonim"
                    )}
                  </strong>
                  <span style={{ opacity: 0.7 }}>•</span>
                  <span style={{ opacity: 0.8, whiteSpace: "nowrap" }}>
                    {new Date(c.createdAt).toLocaleString()}
                  </span>

                  <span style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
                    <CommentLikeButton commentId={c.id} />

                    {/* Admin'e özel 1000 like butonu */}
                    {myIsAdmin && !editing && (
                      <button
                        onClick={() => boostComment(c.id)}
                        disabled={isBoosting}
                        title="Bu yoruma 1000 like ver"
                        style={{ fontSize: 12 }}
                      >
                        {isBoosting ? "Boost…" : "1000 ❤️"}
                      </button>
                    )}

                    {!editing && (
                      <>
                        {allowEdit && (
                          <button onClick={() => startEdit(c)} style={{ fontSize: 12 }}>
                            Düzenle
                          </button>
                        )}
                        {allowDelete && (
                          <button onClick={() => remove(c.id)} style={{ fontSize: 12 }}>
                            Sil
                          </button>
                        )}
                      </>
                    )}
                  </span>
                </div>

                {!editing ? (
                  <>
                    <div style={{ marginTop: 4, color: isGold ? "gold" : "inherit" }}>
                      <RichText text={c.text} />
                    </div>
                    {c.text ? <TranslateButton text={c.text} size="sm" /> : null}
                  </>
                ) : (
                  <div style={{ marginTop: 6, display: "flex", gap: 8 }}>
                    <input
                      value={editText}
                      onChange={(e) => setEditText(e.target.value.slice(0, 500))}
                      onKeyDown={(e) => {
                        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "enter") {
                          e.preventDefault();
                          void saveEdit();
                        } else if (e.key === "Escape") {
                          e.preventDefault();
                          cancelEdit();
                        }
                      }}
                      style={{ flex: 1, padding: 8, borderRadius: 6 }}
                      placeholder="Yorumu düzenle…"
                      maxLength={500}
                    />
                    <button onClick={saveEdit} disabled={busy}>
                      {busy ? "Kaydediliyor…" : "Kaydet"}
                    </button>
                    <button onClick={cancelEdit} disabled={busy}>
                      İptal
                    </button>
                  </div>
                )}

                {editing && err && <div style={{ color: "crimson", marginTop: 6 }}>{err}</div>}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 8 }}>
        <button onClick={loadMore} disabled={!nextAfter || moreLoading}>
          {nextAfter ? (moreLoading ? "Yükleniyor..." : "Daha Fazla Yükle") : "Hepsi bu kadar"}
        </button>
      </div>

      <ImageModal
        open={modal.open}
        src={modal.src}
        alt={modal.username || "avatar"}
        caption={modal.username ? `@${modal.username}` : undefined}
        onClose={() => setModal({ open: false })}
      />
    </div>
  );
}
